import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Auth = ({ mode = 'signup' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === 'login' || location.pathname === '/login');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await signup(formData.email, formData.password, formData.name);
        toast.success('Account created! Welcome to HumanScaled.');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex relative">
      <div className="noise-overlay" />
      
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
        <div className="hero-glow top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-emerald-500 flex items-center justify-center">
              <Zap className="w-7 h-7 text-black" />
            </div>
            <span className="font-black text-3xl tracking-tight">HumanScaled</span>
          </div>
          <h1 className="text-4xl font-bold mb-4 text-gradient">
            The AI Arbitrage Platform
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            We find the jobs. AI does the work. You collect the check. 
            Join thousands of entrepreneurs building AI-powered service businesses.
          </p>
          
          <div className="mt-12 space-y-4">
            {[
              { metric: '10x', label: 'Average ROI for subscribers' },
              { metric: '24/7', label: 'Job board scanning' },
              { metric: '< 5min', label: 'To generate your first pitch' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4">
                <span className="font-mono text-2xl font-bold text-emerald-400 w-20">{item.metric}</span>
                <span className="text-muted-foreground">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-muted-foreground hover:text-white mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>

          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-emerald-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="font-black text-xl tracking-tight">HumanScaled</span>
          </div>

          <h2 className="text-2xl font-bold mb-2">
            {isLogin ? 'Welcome back' : 'Join the waitlist'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {isLogin 
              ? 'Sign in to access your dashboard' 
              : 'Create your account to start finding opportunities'
            }
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="h-12 bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm"
                  data-testid="auth-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12 bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm"
                data-testid="auth-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="h-12 bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm"
                data-testid="auth-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm mt-6"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isLogin ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-6">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                navigate(isLogin ? '/signup' : '/login');
              }}
              className="text-emerald-400 hover:text-emerald-300 font-medium"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;